import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Users, UserCheck, Briefcase, TrendingUp, AlertCircle, Clock,
  Search, Filter, Download, RefreshCw, CheckCircle, XCircle,
  ArrowRight, BarChart3, Activity, Bell, Settings, User,
  FileText, Calendar, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [caseManagers, setCaseManagers] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedCM, setSelectedCM] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Pipeline Overview state
  const [pipelineData, setPipelineData] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'pipeline', 'bigin', 'assignments'
  const [selectedPipelineCM, setSelectedPipelineCM] = useState('all');
  
  // Bigin Pipelines state
  const [biginPipelinesData, setBiginPipelinesData] = useState(null);
  const [biginPipelinesLoading, setBiginPipelinesLoading] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [cmFilter, setCmFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [statusFilter, priorityFilter, cmFilter, searchTerm]);

  // Fetch pipeline data when pipeline tab is selected
  useEffect(() => {
    if (activeTab === 'pipeline' && !pipelineData) {
      fetchPipelineOverview();
    }
    if (activeTab === 'bigin' && !biginPipelinesData) {
      fetchBiginPipelines();
    }
  }, [activeTab]);

  const fetchPipelineOverview = async () => {
    setPipelineLoading(true);
    try {
      const response = await api.get('/admin/pipeline-overview');
      setPipelineData(response.data);
    } catch (error) {
      toast.error('Failed to load pipeline data');
    } finally {
      setPipelineLoading(false);
    }
  };

  const fetchBiginPipelines = async () => {
    setBiginPipelinesLoading(true);
    try {
      const response = await api.get('/admin/bigin-pipelines-overview');
      setBiginPipelinesData(response.data);
    } catch (error) {
      toast.error('Failed to load Bigin pipelines data');
    } finally {
      setBiginPipelinesLoading(false);
    }
  };


  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchStats(),
        fetchPatients(),
        fetchCaseManagers(),
        fetchAssignmentHistory()
      ]);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/assignment-stats');
      setStats(response.data);
    } catch (error) {
    }
  };

  const fetchPatients = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (cmFilter !== 'all') params.append('case_manager_id', cmFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/admin/patients/queue?${params.toString()}`);
      setPatients(response.data.cases || []);
    } catch (error) {
    }
  };

  const fetchCaseManagers = async () => {
    try {
      const response = await api.get('/admin/case-managers/workload');
      setCaseManagers(response.data.case_managers || []);
    } catch (error) {
    }
  };

  const fetchAssignmentHistory = async () => {
    try {
      const response = await api.get('/admin/assignment-history?limit=10');
      setAssignmentHistory(response.data.history || []);
    } catch (error) {
    }
  };

  const handleAssignSingle = async () => {
    if (!selectedCM) {
      toast.error('Please select a case manager');
      return;
    }

    setIsAssigning(true);
    
    // Get CM name before making the request (for immediate feedback)
    const selectedCMObj = caseManagers.find(cm => cm._id === selectedCM);
    const cmDisplayName = selectedCMObj?.full_name || selectedCMObj?.email || 'case manager';
    
    try {
      const response = await api.post('/admin/assign-case', {
        case_id: selectedCase._id,
        case_manager_id: selectedCM,
        notes: assignmentNotes
      });
      
      // Close dialog IMMEDIATELY
      setShowAssignDialog(false);
      setShowReassignDialog(false);
      
      // Show success toast with CM name
      const cmName = response.data?.case_manager_name || cmDisplayName;
      toast.success(`✓ Assigned to ${cmName}!`, {
        duration: 3000,
        position: 'top-right'
      });
      
      // Reset state
      setSelectedCM('');
      setAssignmentNotes('');
      setSelectedCase(null);
      
      // Refresh all data in background
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to assign case';
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedPatients.length === 0) {
      toast.error('Please select patients to assign');
      return;
    }

    if (!selectedCM) {
      toast.error('Please select a case manager');
      return;
    }

    setIsAssigning(true);
    
    // Get CM name before making the request
    const selectedCMObj = caseManagers.find(cm => cm._id === selectedCM);
    const cmDisplayName = selectedCMObj?.full_name || selectedCMObj?.email || 'case manager';
    const patientCount = selectedPatients.length;
    
    try {
      const response = await api.post('/admin/assign-cases-bulk', {
        case_ids: selectedPatients,
        case_manager_id: selectedCM,
        notes: assignmentNotes
      });
      
      // Close dialog IMMEDIATELY
      setShowAssignDialog(false);
      
      const cmName = response.data?.case_manager_name || cmDisplayName;
      const count = response.data?.assigned_count || patientCount;
      toast.success(`✓ ${count} cases assigned to ${cmName}!`, {
        duration: 3000,
        position: 'top-right'
      });
      
      // Reset state
      setSelectedCM('');
      setAssignmentNotes('');
      setSelectedPatients([]);
      
      // Refresh all data in background
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to assign cases';
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedCM) {
      toast.error('Please select a new case manager');
      return;
    }

    setIsAssigning(true);
    
    // Get CM name before making the request
    const selectedCMObj = caseManagers.find(cm => cm._id === selectedCM);
    const newCMName = selectedCMObj?.full_name || selectedCMObj?.email || 'new case manager';
    
    try {
      const response = await api.post('/admin/reassign-case', {
        case_id: selectedCase._id,
        new_case_manager_id: selectedCM,
        reason: assignmentNotes
      });
      
      // Close dialog IMMEDIATELY
      setShowReassignDialog(false);
      
      const fromCM = response.data?.from || selectedCase?.case_manager_name || 'previous CM';
      const toCM = response.data?.to || newCMName;
      toast.success(`✓ Reassigned from ${fromCM} to ${toCM}!`, {
        duration: 3000,
        position: 'top-right'
      });
      
      // Reset state
      setSelectedCM('');
      setAssignmentNotes('');
      setSelectedCase(null);
      
      // Refresh all data in background
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to reassign case';
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  const togglePatientSelection = (caseId) => {
    setSelectedPatients(prev =>
      prev.includes(caseId)
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const selectAllPatients = () => {
    if (selectedPatients.length === patients.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(patients.map(p => p._id));
    }
  };

  const exportToCSV = () => {
    const headers = ['Patient ID', 'Patient Name', 'Treatment Type', 'Current Stage', 'Assignment Status', 'Case Manager', 'Priority'];
    const rows = patients.map(p => [
      p._id,
      p.patient_name,
      p.treatment_type,
      p.current_stage_name || `Stage ${p.current_stage_id}`,
      p.case_manager_id ? 'Assigned' : 'Unassigned',
      p.case_manager_name || 'N/A',
      p.priority || 'medium'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patient-assignments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV successfully!');
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    return status === 'completed' ? 'bg-green-100 text-green-800' :
           status === 'cancelled' ? 'bg-red-100 text-red-800' :
           'bg-blue-100 text-blue-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Super Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="super-admin-dashboard">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50" data-testid="top-nav">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
              <Badge className="bg-purple-100 text-purple-800 text-xs">Assignment Manager</Badge>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="relative"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5" />
                {stats?.unassigned_patients > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {stats.unassigned_patients}
                  </span>
                )}
              </Button>
              <Button variant="ghost" size="sm" data-testid="settings-button">
                <Settings className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2 pl-4 border-l border-gray-200">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold">Super Admin</p>
                  <p className="text-gray-500 text-xs">System Administrator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6" data-testid="kpi-cards">
          <Card 
            className="hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => {
              setStatusFilter('all');
              setCmFilter('all');
              setPriorityFilter('all');
              setSearchTerm('');
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Patients</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_patients || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to view all</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all cursor-pointer hover:scale-105"
            onClick={() => {
              setStatusFilter('assigned');
              setCmFilter('all');
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Cases</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.active_cases || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to view assigned</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all cursor-default">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Case Managers</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_case_managers || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Total available</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all cursor-default">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avg Cases/Manager</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.avg_cases_per_manager || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Average workload</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-all cursor-pointer hover:scale-105 border-red-200"
            onClick={() => {
              setStatusFilter('unassigned');
              setCmFilter('all');
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Unassigned</p>
                  <p className="text-3xl font-bold text-red-600">{stats?.unassigned_patients || 0}</p>
                  <p className="text-xs text-red-600 mt-1">Click to view unassigned</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-overview"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Patient Queue
            </button>
            <button
              onClick={() => setActiveTab('pipeline')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pipeline' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-pipeline"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Pipeline Overview
            </button>
            <button
              onClick={() => setActiveTab('bigin')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bigin' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-bigin"
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Bigin Pipelines
            </button>
            <button
              onClick={() => setActiveTab('assignments')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'assignments' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-assignments"
            >
              <Briefcase className="w-4 h-4 inline mr-2" />
              CM Assignments
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area - Patient Queue */}
          <div className="lg:col-span-2 space-y-4">
            <Card data-testid="patient-queue">
              <CardContent className="p-6">
                {/* Filters and Actions Bar */}
                <div className="mb-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Patient Queue</h2>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchAllData()}
                        data-testid="refresh-button"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={exportToCSV}
                        data-testid="export-button"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export
                      </Button>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search patients..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          data-testid="search-input"
                        />
                      </div>
                    </div>
                    
                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      data-testid="status-filter"
                    >
                      <option value="all">All Status</option>
                      <option value="unassigned">Unassigned</option>
                      <option value="assigned">Assigned</option>
                    </select>

                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      data-testid="priority-filter"
                    >
                      <option value="all">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>

                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={cmFilter}
                      onChange={(e) => setCmFilter(e.target.value)}
                      data-testid="cm-filter"
                    >
                      <option value="all">All Case Managers</option>
                      {caseManagers.map(cm => (
                        <option key={cm._id} value={cm._id}>
                          {cm.full_name || cm.email} ({cm.active_cases})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Bulk Actions */}
                  {selectedPatients.length > 0 && (
                    <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <span className="text-sm text-blue-900 font-medium">
                        {selectedPatients.length} patient(s) selected
                      </span>
                      <Button
                        size="sm"
                        onClick={() => setShowAssignDialog(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="bulk-assign-button"
                      >
                        Bulk Assign
                      </Button>
                    </div>
                  )}
                </div>

                {/* Patient Table */}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="patient-table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedPatients.length === patients.length && patients.length > 0}
                            onChange={selectAllPatients}
                            className="rounded border-gray-300"
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Treatment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stage</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Case Manager</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {patients.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            No patients found
                          </td>
                        </tr>
                      ) : (
                        patients.map((patient) => (
                          <tr key={patient._id} className="hover:bg-gray-50" data-testid={`patient-row-${patient._id}`}>
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={selectedPatients.includes(patient._id)}
                                onChange={() => togglePatientSelection(patient._id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-gray-900">{patient.patient_name}</p>
                                <p className="text-sm text-gray-500">{patient.patient_country}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900">{patient.treatment_type}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-600">
                                Stage {patient.current_stage_id}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <Badge className={getPriorityColor(patient.priority || 'medium')}>
                                {(patient.priority || 'medium').toUpperCase()}
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              {patient.case_manager_name ? (
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{patient.case_manager_name}</p>
                                  <p className="text-xs text-gray-500">{patient.case_manager_email}</p>
                                </div>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">Unassigned</Badge>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center space-x-2">
                                {patient.case_manager_id ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedCase(patient);
                                      setShowReassignDialog(true);
                                    }}
                                    data-testid={`reassign-${patient._id}`}
                                  >
                                    Reassign
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCase(patient);
                                      setShowAssignDialog(true);
                                    }}
                                    data-testid={`assign-${patient._id}`}
                                  >
                                    Assign
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Case Manager Workload Panel */}
            <Card data-testid="workload-panel">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Case Manager Workload</h3>
                <div className="space-y-4">
                  {caseManagers.slice(0, 8).map((cm) => (
                    <div key={cm._id} className="space-y-2" data-testid={`cm-workload-${cm._id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{cm.full_name || cm.email}</p>
                          <p className="text-xs text-gray-500">{cm.active_cases} active cases</p>
                        </div>
                        <Badge className={cm.workload_percentage > 80 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                          {Math.round(cm.workload_percentage)}%
                        </Badge>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${cm.workload_percentage > 80 ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(cm.workload_percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Assignment History */}
            <Card data-testid="assignment-history">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Assignments</h3>
                <div className="space-y-3">
                  {assignmentHistory.slice(0, 10).map((record) => (
                    <div key={record._id} className="flex items-start space-x-3 text-sm">
                      <div className={`mt-1 w-2 h-2 rounded-full ${record.action === 'reassigned' ? 'bg-orange-500' : 'bg-green-500'}`} />
                      <div className="flex-1">
                        <p className="text-gray-900">
                          <span className="font-medium">{record.patient_name}</span>
                          {record.action === 'reassigned' ? ' reassigned to ' : ' assigned to '}
                          <span className="font-medium">{record.case_manager_name}</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(record.assigned_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}

        {/* Pipeline Overview Tab */}
        {activeTab === 'pipeline' && (
          <div className="space-y-6" data-testid="pipeline-overview">
            {pipelineLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading pipeline data...</p>
                </div>
              </div>
            ) : pipelineData ? (
              <>
                {/* Pipeline Stats Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Pipeline Overview</h2>
                    <p className="text-gray-500">View all cases across all case managers by stage</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={selectedPipelineCM}
                      onChange={(e) => setSelectedPipelineCM(e.target.value)}
                    >
                      <option value="all">All Case Managers</option>
                      {pipelineData.case_manager_pipelines?.map(cm => (
                        <option key={cm.case_manager_id} value={cm.case_manager_id}>
                          {cm.case_manager_name} ({cm.total_cases} cases)
                        </option>
                      ))}
                      <option value="unassigned">Unassigned ({pipelineData.unassigned?.total_cases || 0})</option>
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fetchPipelineOverview}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Stage Progress Header */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
                  <div className="flex space-x-2 min-w-max">
                    {pipelineData.journey_stages?.slice(0, 10).map((stage, idx) => {
                      const totalInStage = selectedPipelineCM === 'all' 
                        ? pipelineData.case_manager_pipelines?.reduce((sum, cm) => sum + (cm.stage_counts?.[stage.id] || 0), 0) + (pipelineData.unassigned?.stage_counts?.[stage.id] || 0)
                        : selectedPipelineCM === 'unassigned'
                        ? pipelineData.unassigned?.stage_counts?.[stage.id] || 0
                        : pipelineData.case_manager_pipelines?.find(cm => cm.case_manager_id === selectedPipelineCM)?.stage_counts?.[stage.id] || 0;
                      
                      return (
                        <div key={stage.id} className="flex-shrink-0 w-28 text-center">
                          <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center text-sm font-bold ${
                            totalInStage > 0 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {totalInStage}
                          </div>
                          <p className="text-xs text-gray-600 mt-2 truncate" title={stage.name}>{stage.shortName}</p>
                          {idx < 9 && <ArrowRight className="w-4 h-4 text-gray-300 mx-auto mt-1" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Case Manager Pipeline Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {(selectedPipelineCM === 'all' 
                    ? pipelineData.case_manager_pipelines 
                    : selectedPipelineCM === 'unassigned'
                    ? [{ case_manager_name: 'Unassigned', case_manager_id: 'unassigned', ...pipelineData.unassigned }]
                    : pipelineData.case_manager_pipelines?.filter(cm => cm.case_manager_id === selectedPipelineCM)
                  )?.map(cm => (
                    <Card key={cm.case_manager_id} className="border" data-testid={`pipeline-card-${cm.case_manager_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              cm.case_manager_id === 'unassigned' ? 'bg-gray-200' : 'bg-purple-100'
                            }`}>
                              <UserCheck className={`w-5 h-5 ${cm.case_manager_id === 'unassigned' ? 'text-gray-500' : 'text-purple-600'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{cm.case_manager_name}</p>
                              <p className="text-sm text-gray-500">{cm.case_manager_email || `${cm.total_cases} total cases`}</p>
                            </div>
                          </div>
                          <Badge className={cm.total_cases > 10 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                            {cm.total_cases} cases
                          </Badge>
                        </div>
                        
                        {/* Mini Pipeline View */}
                        <div className="space-y-2">
                          {pipelineData.journey_stages?.slice(0, 8).map(stage => {
                            const count = cm.stage_counts?.[stage.id] || 0;
                            const cases = cm.cases_by_stage?.[stage.id] || [];
                            return count > 0 ? (
                              <div key={stage.id} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
                                <span className="text-gray-600">{stage.shortName}</span>
                                <div className="flex items-center space-x-2">
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{count}</Badge>
                                  {cases.slice(0, 2).map(c => (
                                    <span key={c.case_id} className="text-xs text-gray-500 truncate max-w-[80px]" title={c.patient_name}>
                                      {c.patient_name?.split(' ')[0]}
                                    </span>
                                  ))}
                                  {cases.length > 2 && <span className="text-xs text-gray-400">+{cases.length - 2}</span>}
                                </div>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No pipeline data available</p>
              </div>
            )}
          </div>
        )}

        {/* Bigin Pipelines Tab */}
        {activeTab === 'bigin' && (
          <div className="space-y-6" data-testid="bigin-pipelines">
            {biginPipelinesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading Bigin pipelines...</p>
              </div>
            ) : biginPipelinesData?.success ? (
              <div>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Bigin CRM Pipelines</h2>
                    <p className="text-sm text-gray-500">Live data from Zoho Bigin</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                      Total Deals: {biginPipelinesData.total_deals}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setBiginPipelinesData(null);
                        fetchBiginPipelines();
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Pipeline Cards */}
                <div className="space-y-6">
                  {biginPipelinesData.pipelines?.map((pipeline, idx) => (
                    <Card key={idx} className="overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-white">{pipeline.name}</h3>
                          <Badge className="bg-white/20 text-white border-0">
                            {pipeline.total_deals} Deals
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        {/* Stages Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                          {pipeline.stages?.map((stage, stageIdx) => (
                            <div 
                              key={stageIdx}
                              className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700 truncate" title={stage.name}>
                                  {stage.name}
                                </span>
                                <Badge 
                                  variant={stage.count > 0 ? "default" : "secondary"}
                                  className={stage.count > 0 ? "bg-green-100 text-green-700" : ""}
                                >
                                  {stage.count}
                                </Badge>
                              </div>
                              {stage.count > 0 && (
                                <div className="mt-2 space-y-1">
                                  {stage.deals?.slice(0, 3).map((deal, dealIdx) => (
                                    <div 
                                      key={dealIdx}
                                      className="text-xs text-gray-500 truncate"
                                      title={deal.name}
                                    >
                                      • {deal.name?.replace('[Pre-Arrival] ', '')}
                                    </div>
                                  ))}
                                  {stage.count > 3 && (
                                    <div className="text-xs text-blue-600">
                                      +{stage.count - 3} more
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Bigin Not Configured</h3>
                  <p className="text-gray-500">
                    {biginPipelinesData?.message || 'Unable to fetch Bigin pipeline data'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-6" data-testid="cm-assignments">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Case Manager Assignments</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  fetchCaseManagers();
                  fetchAssignmentHistory();
                }}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Case Manager List */}
              <div className="lg:col-span-2">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Case Managers ({caseManagers.length})</h3>
                    <div className="space-y-4">
                      {caseManagers.map(cm => (
                        <div key={cm._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{cm.full_name || cm.email}</p>
                              <p className="text-sm text-gray-500">{cm.email}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <Badge className="bg-blue-100 text-blue-800 text-xs">{cm.active_cases} active</Badge>
                                <Badge className={cm.workload_percentage > 80 ? 'bg-red-100 text-red-800 text-xs' : 'bg-green-100 text-green-800 text-xs'}>
                                  {Math.round(cm.workload_percentage)}% workload
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCmFilter(cm._id);
                                setActiveTab('overview');
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Cases
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Assignment History */}
              <div>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {assignmentHistory.map((record) => (
                        <div key={record._id} className="flex items-start space-x-3 text-sm border-b border-gray-100 pb-3">
                          <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${record.action === 'reassigned' ? 'bg-orange-500' : 'bg-green-500'}`} />
                          <div className="min-w-0">
                            <p className="text-gray-900 truncate">
                              <span className="font-medium">{record.patient_name}</span>
                              {record.action === 'reassigned' ? ' → ' : ' assigned to '}
                              <span className="font-medium">{record.case_manager_name}</span>
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(record.assigned_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedPatients.length > 1 ? `Assign ${selectedPatients.length} Cases` : 'Assign Case'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Case Manager</label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={selectedCM}
                onChange={(e) => setSelectedCM(e.target.value)}
                data-testid="cm-select"
                disabled={caseManagers.length === 0}
              >
                <option value="">
                  {caseManagers.length === 0 ? 'Loading case managers...' : 'Choose a case manager...'}
                </option>
                {caseManagers.map(cm => (
                  <option key={cm._id} value={cm._id}>
                    {cm.full_name || cm.email} - {cm.active_cases} cases ({Math.round(cm.workload_percentage)}%)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add any notes about this assignment..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                data-testid="assignment-notes"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAssignDialog(false);
                  setSelectedCM('');
                  setAssignmentNotes('');
                }}
                disabled={isAssigning}
              >
                Cancel
              </Button>
              <Button
                onClick={selectedPatients.length > 1 ? handleBulkAssign : handleAssignSingle}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-assign"
                disabled={isAssigning || !selectedCM}
              >
                {isAssigning ? 'Assigning...' : 'Assign'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassignment Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="max-w-md" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedCase && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Current assignment:</p>
                <p className="font-medium text-gray-900">{selectedCase.case_manager_name}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Case Manager</label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={selectedCM}
                onChange={(e) => setSelectedCM(e.target.value)}
              >
                <option value="">Choose a case manager...</option>
                {caseManagers && caseManagers.length > 0 ? (
                  caseManagers.map(cm => (
                    <option key={cm._id} value={cm._id}>
                      {cm.full_name || cm.email} - {cm.active_cases} cases ({Math.round(cm.workload_percentage)}%)
                    </option>
                  ))
                ) : (
                  <option disabled>Loading case managers...</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Reassignment</label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Why is this case being reassigned?"
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowReassignDialog(false)}>Cancel</Button>
              <Button
                onClick={handleReassign}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-reassign"
              >
                Reassign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
